<script setup>
import PriceDisplay from '@/components/Shopify/PriceDisplay.vue'
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useShopify } from '@/stores/shopify';

const {products, selectProduct, relatedId} = defineProps({
  products: {
    type: Array,
    default: () => [],
  },
  relatedId: {
    type: String,
    default: null
  },
  selectProduct: {
    type: Function,
  }
})

const shopify = useShopify()

const _loading = ref(true)
const _products = ref([])
const productArray = ref(products.map(element => element.node))

const initializeProducts = async () => {
  if (relatedId == null) {
    return;
  }

  _loading.value = true
  shopify.fetchRelatedProducts(relatedId)
    .then((response) => {
      _products.value = response.productRecommendations.splice(0, 6);
      productArray.value = _products.value;
    })
    .finally(() => {
      _loading.value = false
    });
}

watch(() => [relatedId], initializeProducts, { immediate: true })

const scrollContainer = useTemplateRef('scrollContainer')

let drag = ref(false)
let startX = 0
let diffX = 0

const dragEnd = () => {
  if (drag.value) {
    drag.value = false

    let start = 1,
      animate = function () {
        let step = Math.sin(start)
        if (step <= 0) {
          window.cancelAnimationFrame(animate)
        } else {
          scrollContainer.value.scrollLeft += diffX * step
          start -= 0.02
          window.requestAnimationFrame(animate)
        }
      }
    animate()
  }
}

const mouseDown = (event) => {
  startX = event.clientX + scrollContainer.value.scrollLeft
  diffX = 0
  event.preventDefault()
}

const mouseMove = (event) => {
  if (event.buttons === 1 && event.button === 0) {
    diffX = startX - (event.clientX + scrollContainer.value.scrollLeft)
    scrollContainer.value.scrollLeft += diffX
    drag.value = true
  }
}

onMounted(() => {
  scrollContainer.value.addEventListener('mousedown', mouseDown)
  scrollContainer.value.addEventListener('mousemove', mouseMove)
  scrollContainer.value.addEventListener('mouseup', dragEnd);
  scrollContainer.value.addEventListener('mouseleave', dragEnd);
})

onBeforeUnmount(() => {
  scrollContainer.value.removeEventListener('mousedown', mouseDown);
  scrollContainer.value.removeEventListener('mousemove', mouseMove);
  scrollContainer.value.removeEventListener('mouseup', dragEnd);
  scrollContainer.value.removeEventListener('mouseleave', dragEnd);
});
</script>

<template>
  <div class="product-scroller" ref="scrollContainer">
    <div class="product-scroller__list" v-if="!relatedId || !_loading">
      <div class="product-scroller__padding"></div>
      <div
        v-for="product in productArray"
        :key="product.id"
        class="product-scroller__item"
        :class="{ disabled: drag }"
        @click="selectProduct(product)"
      >
        <img class="product-scroller__item-image" :src="product.featuredImage.url" alt="" />
        <p class="product-scroller__item-name">{{ product.title }}</p>
        <PriceDisplay
          :price="product.priceRange.minVariantPrice"
          :font-weight="400"
          :font-size="1"
        />
      </div>
      <div class="product-scroller__padding"></div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.product-scroller {
  background-color: var(--clr-white);
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &__list {
    --gap: 66px;
    display: flex;
    // gap: var(--gap);
  }

  &__padding {
    width: var(--scroller-padding, 0);
    flex-shrink: 0;
  }

  &__item {
    width: 274px;
    flex-shrink: 0;
    color: var(--clr-black);
    text-decoration: none;
    cursor: pointer;

    &.disabled {
      pointer-events: none;
      cursor: default;
    }

    & + & {
      margin-left: var(--gap);
    }
  }

  &__item-image {
    width: 100%;
    height: 274px;
    object-fit: cover;
    border-radius: 24px;
    margin-bottom: 20px;
  }

  &__item-name {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }
}
</style>
